import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth, API, resolveImageUrl } from "@/App";
import { toast } from "sonner";
import { 
  Mountain, Award, Users, Calendar, 
  Star, Mail, Target,
  Linkedin, BookOpen, ArrowRight, CheckCircle2, Building2, Globe,
  ChevronLeft, ChevronRight
} from "lucide-react";

const HeroSection = ({ cms }) => {
  return (
    <section className="hero-section" data-testid="hero-section">
      <div 
        className="hero-bg"
        style={{
          backgroundImage: `url('https://static.prod-images.emergentagent.com/jobs/cf190b42-d174-41b1-9a9f-7651cc372e30/images/eed554f28888a7a86bb1fb0afa6693f258dc0b5410cacb2ae884976bcba639d8.png')`
        }}
      />
      <div className="absolute inset-0 bg-gradient-to-r from-slate-900/40 via-slate-900/15 to-transparent" />
      
      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 py-32">
        <div className="max-w-2xl">
          <p className="text-sm uppercase tracking-[0.2em] font-bold text-orange-400 mb-4 animate-fade-in">
            {cms.hero_overline || "Leadership & Executive Coaching"}
          </p>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-serif font-light tracking-tight text-white mb-6 animate-fade-in animation-delay-100">
            {cms.hero_title_line1 || "Reach Your Peak"}<br />
            <span className="font-medium">{cms.hero_title_line2 || "Leadership Potential"}</span>
          </h1>
          <p className="text-lg md:text-xl text-white/80 leading-relaxed mb-8 animate-fade-in animation-delay-200">
            {cms.hero_subtitle || "Transform your leadership journey with personalized executive coaching."}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 animate-fade-in animation-delay-300">
            <Link to="/booking">
              <Button size="lg" className="bg-orange-600 hover:bg-orange-500 text-white text-base font-bold tracking-wide" data-testid="hero-book-btn">
                Schedule Discovery Call
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            <a href="#services">
              <Button size="lg" variant="outline" className="border-white/30 text-white hover:bg-white/10 text-base" data-testid="hero-services-btn">
                Explore Services
              </Button>
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

const AboutSection = ({ cms }) => {
  return (
    <section id="about" className="py-24 md:py-32 bg-stone-50" data-testid="about-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          <div className="relative max-w-md mx-auto lg:mx-0 pb-8">
            <div className="aspect-square w-72 rounded-2xl overflow-hidden shadow-2xl">
              <img 
                src="https://customer-assets.emergentagent.com/job_mountain-executive/artifacts/8l3hcpgf_1690837299357.jpeg"
                alt="Steve Hanson"
                className="w-full h-full object-cover"
              />
            </div>
            <div className="absolute -bottom-4 -right-4 bg-white rounded-xl shadow-xl p-4 max-w-[200px] z-10">
              <p className="text-orange-600 font-bold text-2xl mb-1">15+</p>
              <p className="text-slate-600 text-xs">Years of HR & Leadership Experience</p>
            </div>
            <div className="absolute bottom-6 right-[-80px] w-28 h-28 z-20 drop-shadow-lg">
              <img 
                src="https://customer-assets.emergentagent.com/job_mountain-executive/artifacts/lloyxs33_1649270948616.jpeg"
                alt="Certified Executive Coach"
                className="w-full h-full object-contain rounded-full"
              />
            </div>
          </div>

          <div>
            <p className="text-sm uppercase tracking-[0.2em] font-bold text-orange-600 mb-4">
              {cms.about_overline || "About Steve"}
            </p>
            <h2 className="text-3xl md:text-4xl font-serif font-medium tracking-tight text-slate-900 mb-6">
              {cms.about_title || "Your Partner in Leadership Excellence"}
            </h2>
            <p className="text-base md:text-lg text-slate-600 leading-relaxed mb-6" dangerouslySetInnerHTML={{ __html: cms.about_paragraph1 || "" }} />
            <p className="text-base md:text-lg text-slate-600 leading-relaxed mb-8">
              {cms.about_paragraph2 || ""}
            </p>

            <div className="grid sm:grid-cols-2 gap-4 mb-8">
              {(cms.about_credential1_title || "SHRM-SCP Certified") && (
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center flex-shrink-0">
                  <Award className="h-5 w-5 text-emerald-700" />
                </div>
                <div>
                  <p className="font-semibold text-slate-800">{cms.about_credential1_title || "SHRM-SCP Certified"}</p>
                  <p className="text-sm text-slate-500">{cms.about_credential1_subtitle || "Senior HR Professional"}</p>
                </div>
              </div>
              )}
              {(cms.about_credential2_title || "Certified Executive Coach") && (
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-lg bg-orange-100 flex items-center justify-center flex-shrink-0">
                  <BookOpen className="h-5 w-5 text-orange-600" />
                </div>
                <div>
                  <p className="font-semibold text-slate-800">{cms.about_credential2_title || "Certified Executive Coach"}</p>
                  <p className="text-sm text-slate-500">{cms.about_credential2_subtitle || "Center for Executive Coaching"}</p>
                </div>
              </div>
              )}
              {cms.about_credential3_title && (
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center flex-shrink-0">
                  <Building2 className="h-5 w-5 text-slate-700" />
                </div>
                <div>
                  <p className="font-semibold text-slate-800">{cms.about_credential3_title}</p>
                  <p className="text-sm text-slate-500">{cms.about_credential3_subtitle}</p>
                </div>
              </div>
              )}
              {cms.about_credential4_title && (
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
                  <Globe className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <p className="font-semibold text-slate-800">{cms.about_credential4_title}</p>
                  <p className="text-sm text-slate-500">{cms.about_credential4_subtitle}</p>
                </div>
              </div>
              )}
            </div>

            <div className="flex items-center gap-3 p-4 bg-slate-800 rounded-xl text-white">
              <div className="w-12 h-12 rounded-full bg-white/10 flex items-center justify-center">
                <Users className="h-6 w-6" />
              </div>
              <div>
                <p className="font-semibold">{cms.about_education_title || "Master's in HR & Industrial Relations"}</p>
                <p className="text-sm text-white/70">{cms.about_education_subtitle || "University of Minnesota - Carlson School"}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Photo Gallery & Certification */}
        <div className="mt-20">
          <div className="text-center mb-10">
            <p className="text-sm uppercase tracking-[0.2em] font-bold text-orange-600 mb-3">In Action</p>
            <h3 className="text-2xl md:text-3xl font-serif font-medium text-slate-900">
              Keynote Speaker, Coach & Leader
            </h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-center">
            {/* MBA group on staircase */}
            <div className="rounded-xl overflow-hidden shadow-md hover:shadow-xl transition-shadow">
              <img 
                src="https://customer-assets.emergentagent.com/job_mountain-executive/artifacts/tmmmyeho_1693923981371.jpeg"
                alt="Steve speaking to MBA students"
                className="w-full h-48 md:h-56 object-cover"
              />
              <div className="p-3 bg-white">
                <p className="text-xs font-semibold text-slate-800">MBA Guest Speaker</p>
                <p className="text-xs text-slate-500">Keynote & Leadership Programs</p>
              </div>
            </div>

            {/* Outdoor mountain coaching - CENTER */}
            <div className="rounded-xl overflow-hidden shadow-md hover:shadow-xl transition-shadow">
              <img 
                src="https://customer-assets.emergentagent.com/job_mountain-executive/artifacts/uh2sugeo_1610473037619.jpeg"
                alt="Steve leading outdoor team session"
                className="w-full h-48 md:h-56 object-cover"
              />
              <div className="p-3 bg-white">
                <p className="text-xs font-semibold text-slate-800">Team Development</p>
                <p className="text-xs text-slate-500">Experiential Coaching in the Field</p>
              </div>
            </div>

            {/* Conference group photo */}
            <div className="rounded-xl overflow-hidden shadow-md hover:shadow-xl transition-shadow">
              <img 
                src="https://customer-assets.emergentagent.com/job_mountain-executive/artifacts/gcj8rxzo_1756307268874.jpeg"
                alt="Professional conference group"
                className="w-full h-48 md:h-56 object-cover"
              />
              <div className="p-3 bg-white">
                <p className="text-xs font-semibold text-slate-800">Leadership Conference</p>
                <p className="text-xs text-slate-500">Developing Future Leaders</p>
              </div>
            </div>
          </div>
        </div>

      </div>
    </section>
  );
};

const ServicesSection = ({ packages, cms }) => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const handlePackageClick = (pkg) => {
    if (!user) {
      toast.info("Please login to purchase a package");
      navigate("/login");
      return;
    }
    // Handle checkout
    axios.post(`${API}/checkout`, {
      package_id: pkg.id,
      origin_url: window.location.origin
    }, {
      headers: { Authorization: `Bearer ${localStorage.getItem("token")}` }
    }).then(res => {
      window.location.href = res.data.url;
    }).catch(err => {
      toast.error("Failed to start checkout");
    });
  };

  return (
    <section id="services" className="py-24 md:py-32 bg-white" data-testid="services-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="text-center max-w-2xl mx-auto mb-16">
          <p className="text-sm uppercase tracking-[0.2em] font-bold text-orange-600 mb-4">
            {cms.services_overline || "Coaching Packages"}
          </p>
          <h2 className="text-3xl md:text-4xl font-serif font-medium tracking-tight text-slate-900 mb-6">
            {cms.services_title || "Choose Your Path to the Summit"}
          </h2>
          <p className="text-base md:text-lg text-slate-600 leading-relaxed">
            {cms.services_subtitle || "Every leader's journey is unique."}
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-8">
          {packages.map((pkg, index) => (
            <Card 
              key={pkg.id} 
              className={`service-card relative overflow-hidden ${pkg.is_featured ? 'ring-2 ring-orange-600' : ''}`}
              data-testid={`package-card-${index}`}
            >
              {pkg.is_featured && (
                <div className="absolute top-4 right-4">
                  <Badge className="bg-orange-600 text-white">Most Popular</Badge>
                </div>
              )}
              <div className="h-40 relative overflow-hidden">
                <img 
                  src={pkg.image_url} 
                  alt={pkg.name}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/60 to-transparent" />
                <div className="absolute bottom-4 left-4">
                  <h3 className="text-xl font-serif font-medium text-white">{pkg.name}</h3>
                  <p className="text-sm text-white/80">{pkg.tagline}</p>
                </div>
              </div>
              <CardContent className="p-6">
                <p className="text-slate-600 text-sm mb-6">{pkg.description}</p>
                <ul className="space-y-3 mb-6">
                  {pkg.features.slice(0, 4).map((feature, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-slate-600">
                      <CheckCircle2 className="h-5 w-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                      {feature}
                    </li>
                  ))}
                </ul>
                <Link to="/#contact">
                  <Button 
                    className={`w-full ${pkg.is_featured ? 'bg-orange-600 hover:bg-orange-500' : 'bg-slate-800 hover:bg-slate-700'}`}
                    data-testid={`package-btn-${index}`}
                  >
                    Reach Out for More Info
                  </Button>
                </Link>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
};

const TestimonialsSection = ({ testimonials, cms }) => {
  return (
    <section className="py-24 md:py-32 bg-stone-100" data-testid="testimonials-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="text-center max-w-2xl mx-auto mb-16">
          <p className="text-sm uppercase tracking-[0.2em] font-bold text-orange-600 mb-4">
            {cms.testimonials_overline || "Success Stories"}
          </p>
          <h2 className="text-3xl md:text-4xl font-serif font-medium tracking-tight text-slate-900 mb-6">
            {cms.testimonials_title || "Leaders Who Reached Their Summit"}
          </h2>
        </div>

        <div className="grid md:grid-cols-3 gap-8">
          {testimonials.map((testimonial, index) => (
            <Card key={testimonial.id} className="testimonial-card bg-white" data-testid={`testimonial-${index}`}>
              <CardContent className="p-8">
                <div className="flex items-center gap-4 mb-4">
                  {testimonial.image_url && (
                    <img 
                      src={resolveImageUrl(testimonial.image_url)} 
                      alt={testimonial.name}
                      className="w-12 h-12 rounded-full object-cover"
                    />
                  )}
                  <div>
                    <p className="font-semibold text-slate-900">{testimonial.name}</p>
                    <p className="text-sm text-slate-500">{testimonial.title}, {testimonial.company}</p>
                  </div>
                </div>
                <div className="flex items-center gap-1 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="h-4 w-4 fill-orange-400 text-orange-400" />
                  ))}
                </div>
                <p className="text-slate-600 leading-relaxed">
                  "{testimonial.quote}"
                </p>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
};

const ContactSection = ({ cms }) => {
  const [formData, setFormData] = useState({
    name: "", email: "", phone: "", organization: "",
    title: "", level: "", message: "", service_interest: ""
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        message: `Organization: ${formData.organization}\nTitle: ${formData.title}\nLevel: ${formData.level}\n\n${formData.message}`,
        service_interest: "Contact Form Inquiry"
      });
      setSubmitted(true);
      toast.success("Thank you! We'll be in touch soon.");
    } catch (err) {
      toast.error("Failed to send message. Please try again.");
    }
    setLoading(false);
  };

  return (
    <section id="contact" className="py-24 md:py-32 bg-white" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid lg:grid-cols-2 gap-16">
          <div>
            <p className="text-sm uppercase tracking-[0.2em] font-bold text-orange-600 mb-4">
              {cms.contact_overline || "Get In Touch"}
            </p>
            <h2 className="text-3xl md:text-4xl font-serif font-medium tracking-tight text-slate-900 mb-6">
              {cms.contact_title || "Ready to Begin Your Leadership Journey?"}
            </h2>
            <p className="text-base md:text-lg text-slate-600 leading-relaxed mb-8">
              {cms.contact_subtitle || "Take the first step toward transforming your leadership."}
            </p>

            <div className="space-y-4 mb-8">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-orange-100 flex items-center justify-center">
                  <Mail className="h-5 w-5 text-orange-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Email</p>
                  <p className="font-medium text-slate-900">{cms.contact_email || "steve@stevehansoncoaching.com"}</p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-emerald-100 flex items-center justify-center">
                  <Calendar className="h-5 w-5 text-emerald-700" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Schedule</p>
                  <Link to="/booking" className="font-medium text-emerald-700 hover:underline">
                    Request a Discovery Call
                  </Link>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center">
                  <Linkedin className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">LinkedIn</p>
                  <a href="https://www.linkedin.com/in/steveahanson/" target="_blank" rel="noopener noreferrer" className="font-medium text-blue-600 hover:underline">
                    Connect with Steve
                  </a>
                </div>
              </div>
            </div>
          </div>

          <Card className="bg-stone-50 border-0">
            <CardContent className="p-8">
              {submitted ? (
                <div className="text-center py-12">
                  <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <CheckCircle2 className="h-8 w-8 text-emerald-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">Message Received!</h3>
                  <p className="text-slate-600">Thank you for reaching out. Steve will be in touch within 24-48 hours.</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Name *</label>
                      <Input 
                        value={formData.name}
                        onChange={(e) => setFormData({...formData, name: e.target.value})}
                        required
                        className="h-11"
                        data-testid="contact-name"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Phone *</label>
                      <Input 
                        type="tel"
                        value={formData.phone}
                        onChange={(e) => setFormData({...formData, phone: e.target.value})}
                        placeholder="(555) 123-4567"
                        required
                        className="h-11"
                        data-testid="contact-phone"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Email *</label>
                    <Input 
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      required
                      className="h-11"
                      data-testid="contact-email"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Current Organization *</label>
                    <Input 
                      value={formData.organization}
                      onChange={(e) => setFormData({...formData, organization: e.target.value})}
                      required
                      className="h-11"
                      data-testid="contact-org"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Your Title *</label>
                      <Input 
                        value={formData.title}
                        onChange={(e) => setFormData({...formData, title: e.target.value})}
                        required
                        className="h-11"
                        data-testid="contact-title"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Level *</label>
                      <Select value={formData.level} onValueChange={(v) => setFormData({...formData, level: v})}>
                        <SelectTrigger className="h-11" data-testid="contact-level">
                          <SelectValue placeholder="Select level" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Individual Contributor">Individual Contributor</SelectItem>
                          <SelectItem value="Team Lead">Team Lead</SelectItem>
                          <SelectItem value="Manager">Manager</SelectItem>
                          <SelectItem value="Senior Manager">Senior Manager</SelectItem>
                          <SelectItem value="Director">Director</SelectItem>
                          <SelectItem value="VP">VP</SelectItem>
                          <SelectItem value="SVP">SVP</SelectItem>
                          <SelectItem value="C-Suite">C-Suite</SelectItem>
                          <SelectItem value="Other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Message</label>
                    <Textarea 
                      value={formData.message}
                      onChange={(e) => setFormData({...formData, message: e.target.value})}
                      placeholder="Tell me about your leadership goals..."
                      rows={3}
                      data-testid="contact-message"
                    />
                  </div>
                  <Button 
                    type="submit" 
                    className="w-full h-12 bg-orange-600 hover:bg-orange-500"
                    disabled={loading}
                    data-testid="contact-submit"
                  >
                    {loading ? "Sending..." : "Send Message"}
                  </Button>
                </form>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
  );
};

const Footer = () => {
  return (
    <footer className="bg-slate-900 text-white py-16" data-testid="footer">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid md:grid-cols-4 gap-12 mb-12">
          <div className="md:col-span-2">
            <div className="flex items-center gap-2 mb-4">
              <Mountain className="h-8 w-8 text-orange-500" />
              <span className="font-serif text-xl font-medium">Steve Hanson</span>
            </div>
            <p className="text-slate-400 leading-relaxed max-w-md">
              Leadership & Executive Coach helping leaders reach their peak potential 
              through personalized coaching and proven frameworks.
            </p>
          </div>
          <div>
            <h4 className="font-semibold mb-4">Quick Links</h4>
            <ul className="space-y-2 text-slate-400">
              <li><a href="#about" className="hover:text-white">About</a></li>
              <li><a href="#services" className="hover:text-white">Services</a></li>
              <li><Link to="/content" className="hover:text-white">Resources</Link></li>
              <li><Link to="/coach" className="hover:text-white">AI Coach</Link></li>
            </ul>
          </div>
          <div>
            <h4 className="font-semibold mb-4">Connect</h4>
            <ul className="space-y-2 text-slate-400">
              <li><Link to="/booking" className="hover:text-white">Book a Session</Link></li>
              <li><a href="#contact" className="hover:text-white">Contact</a></li>
              <li>
                <a href="https://www.linkedin.com/in/steveahanson/" target="_blank" rel="noopener noreferrer" className="hover:text-white">
                  LinkedIn
                </a>
              </li>
            </ul>
          </div>
        </div>
        <div className="border-t border-slate-800 pt-8 text-center text-slate-500 text-sm">
          <p>© {new Date().getFullYear()} Steve Hanson Coaching. All rights reserved.</p>
          <p className="mt-1">Coaching approved and in compliance with GFiber Policy standards.</p>
        </div>
      </div>
    </footer>
  );
};

const QuizCTA = () => {
  return (
    <section className="py-20 bg-gradient-to-br from-slate-800 to-slate-900 relative overflow-hidden" data-testid="quiz-cta-section">
      <div className="absolute top-0 right-0 w-96 h-96 bg-orange-500/10 rounded-full blur-3xl" />
      <div className="absolute bottom-0 left-0 w-64 h-64 bg-emerald-500/10 rounded-full blur-3xl" />
      <div className="max-w-7xl mx-auto px-6 md:px-12 relative">
        <div className="flex flex-col md:flex-row items-center gap-12">
          <div className="flex-1">
            <div className="inline-flex items-center gap-2 bg-orange-500/20 text-orange-400 px-4 py-2 rounded-full text-sm font-bold mb-6">
              <Target className="h-4 w-4" />
              Free Assessment
            </div>
            <h2 className="text-3xl md:text-4xl font-serif font-medium text-white mb-4">
              How Strong Is Your Leadership?
            </h2>
            <p className="text-lg text-white/70 leading-relaxed mb-8">
              Take our free 3-minute leadership assessment and discover your strengths 
              across communication, strategy, team building, emotional intelligence, 
              and change management.
            </p>
            <Link to="/quiz">
              <Button size="lg" className="bg-orange-600 hover:bg-orange-500 text-base font-bold" data-testid="quiz-cta-btn">
                Take the Free Assessment
                <ArrowRight className="h-5 w-5 ml-2" />
              </Button>
            </Link>
          </div>
          <div className="flex-shrink-0">
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-5 text-center">
                <p className="text-2xl font-bold text-white mb-1">10</p>
                <p className="text-xs text-white/60">Questions</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-5 text-center">
                <p className="text-2xl font-bold text-white mb-1">3</p>
                <p className="text-xs text-white/60">Minutes</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-5 text-center">
                <p className="text-2xl font-bold text-white mb-1">5</p>
                <p className="text-xs text-white/60">Categories</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-5 text-center">
                <p className="text-2xl font-bold text-orange-400 mb-1">Free</p>
                <p className="text-xs text-white/60">Always</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const LandingPage = () => {
  const [packages, setPackages] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [cms, setCms] = useState({});

  useEffect(() => {
    axios.get(`${API}/packages`).then(res => setPackages(res.data)).catch(() => {});
    axios.get(`${API}/testimonials`).then(res => setTestimonials(res.data)).catch(() => {});
    axios.get(`${API}/site-content`).then(res => setCms(res.data)).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen">
      <HeroSection cms={cms} />
      <AboutSection cms={cms} />
      <QuizCTA />
      {packages.length > 0 && <ServicesSection packages={packages} cms={cms} />}
      {testimonials.length > 0 && <TestimonialsSection testimonials={testimonials} cms={cms} />}
      <ContactSection cms={cms} />
      <Footer />
    </div>
  );
};

export default LandingPage;
